import React, { ReactNode, useEffect, useState } from 'react';
import startCase from 'lodash/startCase';
import QuestionButton from 'src/shared/components/question-button/QuestionButton';
import cmsService from 'src/services/cms-service';
import styles from './QuestionButtonHelp.module.scss';
import Tooltip from '../tooltip/Tooltip';
import HelpPopup from '../help-popup/HelpPopup';
import CMSTechincalExplainer from '../cms-technical-explainer/CMSTechnicalExplainer';

export type CommonQuestionButtonHelpProps = {
    label?: string;
    placement?: 'top' | 'right' | 'bottom' | 'left';
    maxWidth?: string | number;
    padding?: string | number;
    showHelpTitle?: boolean;
    detailedExplainerSlug?: string;
    showDetailedExplainerLink?: boolean;
    icon?: ReactNode;
    popperPropsOverride?: never;
    zIndex?: number;
};

export type QuestionButtonHelpProps =
    | (CommonQuestionButtonHelpProps & {
          type: 'custom';
          helpText: ReactNode;
          onTooltipHover?: () => void;
          onDetailedHelpClick?: (explainerSlug: string) => void;
      })
    | (CommonQuestionButtonHelpProps & {
          type: 'api';
          slug: string | string[];
          onTooltipHover?: () => void;
          onDetailedHelpClick?: (explainerSlug: string) => void;
      });

const QuestionButtonHelp = (props: QuestionButtonHelpProps) => {
    const { placement = 'right', type, onDetailedHelpClick, onTooltipHover, showDetailedExplainerLink = true } = props;
    const [cmsData, setCMSData] = useState<{
        content: ReactNode;
        title: ReactNode;
    } | null>(null);

    const [cmsDetailedData, setCMSDetailedData] = useState<{
        content: ReactNode;
        title: ReactNode;
    } | null>(null);

    const [open, setOpen] = useState<boolean>(false);
    const [shouldShowModal, showModal] = useState<boolean>(false);
    useEffect(() => {
        if (props.type === 'custom') {
            setCMSData({
                content: props.helpText,
                title: props.label,
            });
            return;
        }

        const explainerSlug = props.detailedExplainerSlug || `${props.slug}-te`;
        const fetchDetailedHelpContent = async () => {
            const { content, title } = (await cmsService.getArticleBySlug(explainerSlug)) || {};
            setCMSDetailedData({ content, title: title || props.label });
        };
        fetchDetailedHelpContent();

        const fetchHelpContent = async () => {
            // If type of slug is string
            if (typeof props.slug === 'string') {
                const { content, title } = (await cmsService.getArticleBySlug(props.slug)) || {};
                setCMSData({ content, title: title || props.label });
                return;
            }

            // If type of slug is array
            const contentArray = props.slug.map(async slug => {
                const { content, title } = (await cmsService.getArticleBySlug(slug)) || {};
                return { content, title };
            });
            const allContent = await Promise.all(contentArray);
            const mergedContent = allContent.map(content => content.content).join('<p></p>');
            setCMSData({ content: mergedContent, title: props.label || '' });
        };

        fetchHelpContent();
    }, [props]);

    if (!cmsData) {
        return null;
    }

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(true);
        onTooltipHover?.();
    };

    const content = type === 'custom' ? cmsData.content : <span dangerouslySetInnerHTML={{ __html: cmsData.content as string }} />;

    const contentWithTitle = (
        <>
            {props.showHelpTitle && <div className={styles.title}>{cmsData.title && startCase(cmsData.title.toString())}</div>}
            {content}
            {showDetailedExplainerLink && cmsDetailedData?.content && cmsDetailedData?.title && (
                <HelpPopup
                    popupClassNames={{ body: styles.detailedContentbody }}
                    label={
                        <span className={styles.detailedPopupLabel}>
                            <div>View detailed description</div>
                        </span>
                    }
                    onOpen={() => {
                        showModal(true);
                        onDetailedHelpClick?.(props.detailedExplainerSlug || '');
                    }}
                    onClose={() => {
                        showModal(false);
                        handleTooltipClose();
                    }}
                >
                    <CMSTechincalExplainer title={cmsDetailedData.title} content={cmsDetailedData.content} />
                </HelpPopup>
            )}
        </>
    );

    return (
        <div
            className={styles.label}
            onMouseEnter={handleTooltipOpen}
            onMouseLeave={shouldShowModal ? undefined : handleTooltipClose}
            data-explainer-icon='true'
        >
            {props.label && <span className={styles.labelTitle}>{props.label}</span>}
            <Tooltip
                placement={placement}
                content={contentWithTitle}
                isOpen={open}
                maxWidth={props.maxWidth}
                padding={props.padding}
                zIndex={props.zIndex}
            >
                {props.icon || <QuestionButton />}
            </Tooltip>
        </div>
    );
};

export default QuestionButtonHelp;
